import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router';
import Button from '@mui/material/Button';
import { Box } from '@mui/system';
import { GridCallbackDetails, GridPaginationModel } from '@mui/x-data-grid';

import { QueryRenderer } from '@barboxx/admin-components';
import {
    useBusinessSelector,
    usePaging,
    useSearchString,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Can, useBool } from '@barboxx/react-utils';

import {
    CustomPageHeader,
    RootContainer,
} from '../../components/nav/RootContainer';
import { CreateBusinessDefaultProductDialog } from './component/CreateDialog';
import { BusinessDefaultProductTable } from './component/DefaultProductTable';

export function BusinessDefaultProductList() {
    const { page, size, updatePageModel, sort, updateSort } = usePaging();
    const navigate = useNavigate();
    const [createOpen, createActions] = useBool();
    const { businessId } = useBusinessSelector();

    const { InputComponent, searchString } = useSearchString();

    const businessDefaultProductQuery = useQuery({
        queryKey: [
            'businessDefaultProducts',
            page,
            size,
            sort,
            searchString,
            businessId,
        ],
        queryFn: () =>
            Api.BusinessSpecific.DefaultProduct.getDefaultProducts({
                page,
                size,
                sort,
                searchString,
                businessId,
            }),
    });

    const handleChangePageModel = (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => {
        updatePageModel(model);
    };

    const handleRowClick = (id: string) => {
        navigate(`${id}`);
    };

    const handleCreate = React.useCallback(() => {
        createActions.on();
    }, [createActions]);

    const handleSuccess = () => {
        businessDefaultProductQuery.refetch();
    };

    const CustomPageToolbarComponent = React.useCallback(
        () => (
            <CustomPageHeader>
                <Box gap={2} flexDirection="row" display="flex">
                    <InputComponent />
                    <Can I="create" a="BusinessDefaultProduct">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreate}
                        >
                            Add Default Product
                        </Button>
                    </Can>
                </Box>
            </CustomPageHeader>
        ),
        [InputComponent, handleCreate]
    );

    return (
        <RootContainer
            maxWidth="md"
            slots={{ header: CustomPageToolbarComponent }}
        >
            <QueryRenderer results={businessDefaultProductQuery}>
                {data => (
                    <BusinessDefaultProductTable
                        data={data?.results ?? []}
                        size={size}
                        handleChangePageModel={handleChangePageModel}
                        onRowClick={handleRowClick}
                        onChangeSort={updateSort}
                        page={page}
                        total={data?.total ?? 0}
                    />
                )}
            </QueryRenderer>

            <CreateBusinessDefaultProductDialog
                open={createOpen}
                onClose={createActions.off}
                onSuccess={handleSuccess}
            />
        </RootContainer>
    );
}
